/**
 * Módulo de autenticación — Presupuesto Pro
 * ─────────────────────────────────────────────────────────────────
 * Seguridad implementada:
 *  • Contraseña almacenada como SHA-256 (nunca en texto plano)
 *  • Protección anti-fuerza-bruta (5 intentos → bloqueo 15 min)
 *  • Delay progresivo exponencial entre intentos fallidos
 *  • Sesión vinculada al fingerprint del navegador
 *  • Auto-cierre por inactividad (30 min)
 *  • Token de sesión criptográficamente aleatorio
 */

import {
  getBrowserFingerprint,
  recordFailedAttempt,
  clearLoginAttempts,
  loginDelay,
  isLockedOut,
} from './security';

// ── Constantes ────────────────────────────────────────────────────────────────
const SESSION_KEY       = 'pp_session';
const SESSION_DURATION  = 8 * 60 * 60 * 1000;   // 8 horas
const INACTIVITY_LIMIT  = 30 * 60 * 1000;        // 30 min inactividad → logout
const ACTIVITY_KEY      = 'pp_last_activity';

// Hash SHA-256 de la contraseña maestra (nunca en texto plano)
const PASSWORD_HASH = '148f4e446e3611c4730295cb849311f1c6d3b46a008917d8068f2e07dc7aee69';

// ── Utilidades criptográficas ─────────────────────────────────────────────────
async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function randomToken(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── Estructura de sesión ──────────────────────────────────────────────────────
interface Session {
  token: string;        // token aleatorio para unicidad
  expiresAt: number;    // caducidad absoluta (8 h)
  fingerprint: string;  // huella del navegador para binding
}

// ── Autenticación ─────────────────────────────────────────────────────────────
/**
 * Verifica la contraseña con protección anti-fuerza-bruta:
 *  1. Comprueba si hay bloqueo activo → rechaza sin procesar
 *  2. Aplica delay progresivo según intentos previos
 *  3. Compara hash SHA-256
 *  4. En caso de fallo: registra intento y puede activar bloqueo
 *  5. En caso de éxito: limpia contador de intentos
 */
export async function checkPassword(
  password: string
): Promise<{ ok: boolean; locked: boolean; attempts: number }> {
  // 1. Bloqueo activo
  if (isLockedOut()) {
    return { ok: false, locked: true, attempts: 5 };
  }

  // 2. Delay progresivo (evita ataques automatizados)
  const currentAttempts = parseInt(localStorage.getItem('pp_auth_fail') ?? '0', 10);
  await loginDelay(currentAttempts);

  // 3. Hash y comparación
  const hash = await sha256(password);
  const ok = hash === PASSWORD_HASH;

  if (ok) {
    // 5. Éxito: resetear intentos
    clearLoginAttempts();
    return { ok: true, locked: false, attempts: 0 };
  } else {
    // 4. Fallo: registrar
    const result = recordFailedAttempt();
    return { ok: false, locked: result.locked, attempts: result.attempts };
  }
}

// ── Gestión de sesión ─────────────────────────────────────────────────────────
export async function createSession(): Promise<void> {
  const fingerprint = await getBrowserFingerprint();
  const s: Session = {
    token: randomToken(),
    expiresAt: Date.now() + SESSION_DURATION,
    fingerprint,
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
  updateActivity();
}

export function isSessionValid(): boolean {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return false;

    const s: Session = JSON.parse(raw);

    // Caducidad absoluta
    if (Date.now() >= s.expiresAt) {
      destroySession();
      return false;
    }

    // Inactividad
    const lastActivity = parseInt(sessionStorage.getItem(ACTIVITY_KEY) ?? '0', 10);
    if (lastActivity > 0 && Date.now() - lastActivity > INACTIVITY_LIMIT) {
      destroySession();
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/** Verificación ampliada: incluye comprobación de fingerprint */
export async function isSessionValidSecure(): Promise<boolean> {
  if (!isSessionValid()) return false;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    const s: Session = JSON.parse(raw);
    if (!s.fingerprint) return true; // sesiones antiguas sin fingerprint: permitir
    const currentFp = await getBrowserFingerprint();
    if (s.fingerprint !== currentFp) {
      // Fingerprint cambiado → sesión inválida (posible robo de sesión)
      destroySession();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function destroySession(): void {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(ACTIVITY_KEY);
}

/** Registra la última actividad del usuario (para el timeout de inactividad) */
export function updateActivity(): void {
  sessionStorage.setItem(ACTIVITY_KEY, String(Date.now()));
}

/** Devuelve info de la sesión para mostrar en UI */
export function getSessionInfo(): { expiresAt: number; lastActivity: number } | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s: Session = JSON.parse(raw);
    const lastActivity = parseInt(sessionStorage.getItem(ACTIVITY_KEY) ?? '0', 10);
    return { expiresAt: s.expiresAt, lastActivity };
  } catch {
    return null;
  }
}
